import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from "@angular/core";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { s25Resource } from "../../../util/s25.resource";
import { ContactService } from "../../../services/contact.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { SeProfileUiStore } from "./se.profile.ui.store";

@Component({
    standalone: true,
    selector: "s25-ng-contact-profile-image",
    host: {
        "[class.contact-profile-image--interactive]": "interactive()",
    },
    template: `
        <div class="contact-image-container" [attr.title]="interactive() ? interactiveTooltip() : null">
            @if (storedImageValue()) {
                <s25-ng-filestore-image
                    [value]="storedImageValue()"
                    [readOnly]="true"
                    [circle]="true"
                    [circleSizePx]="size()"
                />
            } @else {
                <div class="contact-image-placeholder" [style.width.px]="size()" [style.height.px]="size()">
                    <s25-ng-icon [type]="'contactFilled'" [size]="iconSize()" [ariaLabel]="'Contact placeholder'" />
                </div>
            }
        </div>
    `,
    styles: `
        .contact-image-container {
            flex-shrink: 0;
            position: relative;
        }

        .contact-image-placeholder {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            border: 3px solid #f0f0f0;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        ::ng-deep .contact-image-placeholder svg {
            opacity: 0.6;
            color: #6c757d;
        }

        :host.contact-profile-image--interactive .contact-image-container {
            border-radius: 50%;
            transition:
                box-shadow 0.2s ease,
                transform 0.2s ease;
        }

        :host.contact-profile-image--interactive .contact-image-container:hover {
            cursor: pointer;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.45);
            transform: scale(1.04);
        }

        @media (max-width: 768px) {
            .contact-image-container {
                align-self: center;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25IconComponent, S25FilestoreImageComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25ContactProfileImageComponent {
    private readonly seProfileUiStore = inject(SeProfileUiStore);

    readonly contactId = input.required<number>();
    readonly name = input.required<string | undefined>();

    /** Optional raw or display image value; when omitted, loads from {@link ContactService.getContactImagePath}. */
    readonly inputValue = input<string | undefined>(undefined, { alias: "imageUrl" });

    /** When true, hover shows pointer + ring so the image reads as clickable (e.g. profile settings). */
    readonly interactive = input(false);

    /** Native tooltip when {@link interactive} is true (omit when the control is not a direct link to settings). */
    readonly interactiveTooltip = input<string | undefined>(undefined);

    /** Diameter in px for the circular image; defaults to 80. */
    readonly size = input(80);

    readonly iconSize = computed(() => Math.round(this.size() * 0.45));

    readonly seProfileImageOverride = computed(() => {
        if (!this.seProfileUiStore.isSeActive()) return undefined;
        if (this.seProfileUiStore.currentContactId() !== this.contactId()) return undefined;
        return this.seProfileUiStore.profileImageValue();
    });

    readonly urlResource = s25Resource({
        if: () => this.seProfileUiStore.isSeActive() && !this.inputValue() && !this.seProfileImageOverride(),
        params: () => this.contactId(),
        loader: async ({ params: contactId, abortSignal }) => {
            const results = await ContactService.getContactImagePathDebounced([contactId]);
            return results?.get(contactId) ?? "";
        },
    });

    readonly storedImageValue = computed(
        () => this.inputValue() ?? this.seProfileImageOverride() ?? this.urlResource.value() ?? "",
    );
}
